/**
 * Auth library for NovelReader Admin
 * Handles authentication with better-auth backend using magic links
 */

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8787";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface User {
  id: string;
  email: string;
  name?: string;
}

export interface Session {
  id: string;
  userId: string;
  expiresAt: string;
}

export interface AuthResponse {
  user: User;
  session?: Session;
}

export interface SessionResponse {
  user: User;
  session: Session;
}

// ---------------------------------------------------------------------------
// Auth Functions
// ---------------------------------------------------------------------------

/**
 * Send a magic link to the user's email
 * Works for both new and existing users
 * After verification, user is redirected to the frontend (callbackURL)
 */
export async function sendMagicLink(email: string): Promise<void> {
  // Use current origin as callback - works for local dev, preview, and production
  const callbackURL = window.location.origin;

  const response = await fetch(`${API_BASE}/api/auth/sign-in/magic-link`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, callbackURL }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to send magic link");
  }
}

/**
 * Sign out the current user
 */
export async function signOut(): Promise<void> {
  const response = await fetch(`${API_BASE}/api/auth/sign-out`, {
    method: "POST",
    credentials: "include",
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Logout failed");
  }
}

/**
 * Get the current user session
 * Returns null if not authenticated
 */
export async function getSession(): Promise<SessionResponse | null> {
  const response = await fetch(`${API_BASE}/api/auth/get-session`, {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    return null;
  }

  const data = await response.json();

  // better-auth returns null/undefined if no session
  if (!data?.user) {
    return null;
  }

  return data;
}

/**
 * Update the current user's profile (name, image)
 * Uses better-auth's update-user endpoint
 */
export async function updateUser(data: {
  name?: string;
  image?: string;
}): Promise<User> {
  const response = await fetch(`${API_BASE}/api/auth/update-user`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to update user");
  }

  const result = await response.json();
  return result.user;
}
